import { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import { Container, Typography, Box } from '@mui/material';
import gsap from "gsap";

function PrivacyPolicy() {
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (pageRef.current) {
      gsap.fromTo(
        pageRef.current,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.8, ease: "power2.out" }
      );
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>Privacy Policy | CAMILLACINODESIGN&DEV</title>
        <meta name="description" content="Informativa sulla privacy di camillacinodesign.dev ai sensi del Regolamento UE 2016/679 (GDPR)." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <Box
        ref={pageRef}
        sx={{
          minHeight: '100vh',
          pt: { xs: 14, md: 18 },
          pb: 10,
          backgroundColor: '#101010',
          color: '#fff',
        }}
      >
        <Container maxWidth="xl">
          <Typography
            variant="h2"
            sx={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.8rem' },
              mb: 2,
              background: 'linear-gradient(135deg, #048091, #7E82F7)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Privacy Policy
          </Typography>

          <Typography
            variant="body2"
            sx={{
              fontFamily: "'Space Grotesk', sans-serif",
              color: 'rgba(255,255,255,0.5)',
              mb: 6,
            }}
          >
            Ultimo aggiornamento: 19 agosto 2026
          </Typography>

          <Section title="1. Titolare del trattamento">
            <p>
              Il titolare del trattamento dei dati personali è <strong>Camilla Cino - CAMILLACINODESIGN&DEV</strong>,
              con sede legale in via Per S. Felice 3, Camposanto (MO) 41031, P.IVA 04601200167.
            </p>
            <p>
              Email di contatto: <a href="mailto:info@camillacinodesigndev.it">info@camillacinodesigndev.it</a>
            </p>
          </Section>

          <Section title="2. Tipologie di dati raccolti">
            <p>
              Il sito <strong>camillacinodesign.dev</strong> raccoglie le seguenti tipologie di dati:
            </p>

            <SubSection title="2.1 Dati forniti volontariamente">
              <ul>
                <li>Nome, cognome, indirizzo email e messaggio inviati tramite il modulo di contatto e prenotazione.</li>
                <li>Dati forniti tramite comunicazione diretta via email o telefono.</li>
              </ul>
            </SubSection>

            <SubSection title="2.2 Dati raccolti automaticamente">
              <ul>
                <li>Dati di navigazione: indirizzo IP, tipo di browser, sistema operativo, pagine visitate, orario di accesso.</li>
                <li>Cookie tecnici necessari al funzionamento del sito.</li>
                <li>Cookie analitici (se accettati dall'utente) per analisi statistiche aggregate.</li>
              </ul>
            </SubSection>
          </Section>

          <Section title="3. Finalità del trattamento">
            <p>I dati personali sono trattati per le seguenti finalità:</p>
            <ul>
              <li>Rispondere alle richieste di contatto e preventivo.</li>
              <li>Gestire la prenotazione di appuntamenti conoscitivi.</li>
              <li>Analisi statistica anonima del traffico sul sito.</li>
              <li>Adempimento di obblighi legali e fiscali.</li>
              <li>Miglioramento dell'esperienza di navigazione.</li>
            </ul>
          </Section>

          <Section title="4. Base giuridica del trattamento">
            <ul>
              <li><strong>Consenso</strong> dell'interessato (art. 6.1.a GDPR): per l'invio di comunicazioni e l'uso di cookie non tecnici.</li>
              <li><strong>Esecuzione di un contratto o misure precontrattuali</strong> (art. 6.1.b GDPR): per rispondere a richieste di preventivo.</li>
              <li><strong>Interesse legittimo</strong> del titolare (art. 6.1.f GDPR): per garantire la sicurezza e il funzionamento del sito.</li>
              <li><strong>Obbligo legale</strong> (art. 6.1.c GDPR): per adempimenti fiscali e normativi.</li>
            </ul>
          </Section>

          <Section title="5. Modalità di trattamento">
            <p>
              I dati sono trattati con strumenti informatici e/o telematici, con logiche strettamente correlate
              alle finalità indicate e con adozione di adeguate misure di sicurezza per prevenire accessi non
              autorizzati, perdita o distruzione dei dati.
            </p>
          </Section>

          <Section title="6. Comunicazione e diffusione dei dati">
            <p>I dati personali non saranno diffusi. Potranno essere comunicati a:</p>
            <ul>
              <li>Fornitori di servizi tecnici necessari al funzionamento del sito (hosting, email).</li>
              <li>Autorità competenti, se richiesto per legge.</li>
            </ul>
          </Section>

          <Section title="7. Conservazione dei dati">
            <p>
              I dati personali sono conservati per il tempo strettamente necessario alle finalità per cui
              sono stati raccolti, e comunque non oltre i termini previsti dalla normativa vigente:
            </p>
            <ul>
              <li>Dati da modulo di contatto: 24 mesi dall'ultima comunicazione.</li>
              <li>Dati di navigazione: 14 mesi (cookie analitici).</li>
              <li>Dati fiscali/contabili: 10 anni come previsto dalla normativa fiscale italiana.</li>
            </ul>
          </Section>

          <Section title="8. Diritti dell'interessato">
            <p>
              Ai sensi degli artt. 15-22 del Regolamento UE 2016/679, l'utente può in qualsiasi momento esercitare i seguenti diritti:
            </p>
            <ul>
              <li><strong>Accesso:</strong> ottenere conferma dell'esistenza di un trattamento e accedere ai propri dati.</li>
              <li><strong>Rettifica:</strong> aggiornare o correggere dati inesatti.</li>
              <li><strong>Cancellazione:</strong> richiedere la cancellazione dei propri dati ("diritto all'oblio").</li>
              <li><strong>Limitazione:</strong> limitare il trattamento in determinati casi.</li>
              <li><strong>Portabilità:</strong> ricevere i propri dati in formato strutturato e leggibile.</li>
              <li><strong>Opposizione:</strong> opporsi al trattamento per motivi legittimi.</li>
              <li><strong>Revoca del consenso:</strong> revocare in qualsiasi momento il consenso prestato.</li>
            </ul>
            <p>
              Per esercitare i propri diritti, scrivere a: <a href="mailto:info@camillacinodesigndev.it">info@camillacinodesigndev.it</a>
            </p>
          </Section>

          <Section title="9. Servizi di terze parti">
            <p>Il sito utilizza i seguenti servizi esterni:</p>
            <ul>
              <li><strong>EmailJS</strong> — per l'invio di email dal modulo di contatto. I dati transitano sui server di EmailJS.</li>
            </ul>
          </Section>

          <Section title="10. Trasferimento dati extra-UE">
            <p>
              Alcuni dei servizi terzi utilizzati (EmailJS) possono comportare il trasferimento
              di dati verso paesi al di fuori dell'Unione Europea. In tali casi, il trasferimento avviene
              nel rispetto delle garanzie previste dal GDPR (Capo V), incluse le Clausole Contrattuali Standard
              approvate dalla Commissione Europea e/o l'adesione al Data Privacy Framework.
            </p>
          </Section>

          <Section title="11. Modifiche alla presente policy">
            <p>
              Il titolare si riserva il diritto di apportare modifiche alla presente informativa in qualsiasi momento.
              Le modifiche saranno pubblicate su questa pagina con aggiornamento della data di ultima revisione.
              Si consiglia di consultare periodicamente questa pagina.
            </p>
          </Section>

          <Section title="12. Riferimenti normativi">
            <ul>
              <li>Regolamento UE 2016/679 (GDPR)</li>
              <li>D.Lgs. 196/2003 (Codice Privacy), come modificato dal D.Lgs. 101/2018</li>
              <li>Provvedimenti del Garante per la Protezione dei Dati Personali</li>
            </ul>
          </Section>

          <Section title="13. Contatti">
            <Box
              sx={{
                backgroundColor: 'rgba(4, 128, 145, 0.1)',
                border: '1px solid rgba(4, 128, 145, 0.3)',
                borderRadius: 2,
                p: 3,
                mt: 2,
              }}
            >
              <p style={{ margin: 0 }}>
                <strong>Camilla Cino - CAMILLACINODESIGN&DEV</strong><br />
                Via Per S. Felice 3, Camposanto (MO) 41031<br />
                P.IVA: 04601200167<br />
                Email: <a href="mailto:info@camillacinodesigndev.it" style={{ color: '#048091' }}>info@camillacinodesigndev.it</a><br />
                Sito: <a href="https://www.camillacinodesign.dev" style={{ color: '#048091' }}>www.camillacinodesign.dev</a>
              </p>
            </Box>
          </Section>

        </Container>
      </Box>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 5 }}>
      <Typography
        variant="h5"
        sx={{
          fontFamily: "'Space Grotesk', sans-serif",
          fontWeight: 600,
          fontSize: { xs: '1.2rem', md: '1.4rem' },
          mb: 2,
          color: '#fff',
        }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          fontFamily: "'Space Grotesk', sans-serif",
          fontSize: '0.95rem',
          lineHeight: 1.8,
          color: 'rgba(255,255,255,0.8)',
          '& p': { mb: 2, mt: 0 },
          '& ul': { pl: 3, mb: 2 },
          '& li': { mb: 1 },
          '& a': {
            color: '#048091',
            textDecoration: 'underline',
            '&:hover': { color: '#7E82F7' },
          },
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="h6"
        sx={{
          fontFamily: "'Space Grotesk', sans-serif",
          fontWeight: 500,
          fontSize: '1rem',
          mb: 1,
          color: 'rgba(255,255,255,0.9)',
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
}

export default PrivacyPolicy;
